import React, { ReactNode, useCallback, useRef, useState } from 'react';
import {
  Select as SelectComponent,
  MenuItem,
  FormControl,
  InputLabel,
  SelectProps,
  FormHelperText,
  useTheme,
  Typography,
  Box
} from '@mui/material';
import { Controller, Control } from 'react-hook-form';
import { FaRegCirclePlay } from 'react-icons/fa6';
import { MdStopCircle } from 'react-icons/md';
import { BiPlus } from 'react-icons/bi';

export interface Option {
  value: string | number;
  label: string | ReactNode;
  disabled?: boolean;
  selected?: boolean;
  audioUrl?: string;
}

interface Props extends Omit<SelectProps, 'name' | 'label'> {
  name: string;
  control: Control<any>;
  label: string;
  isLoading?: boolean;
  options: Option[];
  onScrollEnd?: () => void;
  handleAddNewOption?: () => void;
}

export default function Select({
  name,
  control,
  label,
  isLoading,
  options,
  onScrollEnd,
  handleAddNewOption,
  ...selectProps
}: Props) {
  const theme = useTheme();
  const audioRefs = useRef<{ [key: string]: HTMLAudioElement | null }>({});
  const [playingAudio, setPlayingAudio] = useState<string | number | null>(null);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      if (onScrollEnd) {
        const bottom =
          event.currentTarget.scrollHeight - event.currentTarget.scrollTop === event.currentTarget.clientHeight;
        if (bottom) {
          onScrollEnd();
        }
      }
    },
    [onScrollEnd]
  );

  const handlePlayAudio = (audioUrl?: string, optionValue?: string | number) => {
    if (!audioUrl || !optionValue) return;

    if (playingAudio === optionValue) {
      const audio = audioRefs.current[optionValue];
      if (audio) audio.pause();
      setPlayingAudio(null);
      return;
    }

    Object.values(audioRefs.current).forEach((audio) => {
      if (audio && !audio.paused) audio.pause();
    });

    const audio = audioRefs.current[optionValue];
    if (audio) {
      audio.currentTime = 0;
      audio.play();
      setPlayingAudio(optionValue);
      audio.onended = () => setPlayingAudio(null);
    }
  };

  return (
    <FormControl fullWidth size={selectProps.size}>
      <InputLabel sx={{ display: 'flex', gap: 0.5 }} id={`${name}-label`}>
        {label} <Typography color='#DA0808'> {selectProps.required && '*'} </Typography>
      </InputLabel>
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => {
          return (
            <>
              <SelectComponent
                {...field}
                labelId={`${name}-label`}
                label={label}
                error={!!error}
                {...selectProps}
                MenuProps={{
                  PaperProps: {
                    onScroll: handleScroll,
                    style: { maxHeight: 250 }
                  }
                }}
                renderValue={(selected) => {
                  if (!Array.isArray(selected)) {
                    const selectedOption = options.find((option) => {
                      const isNumber = typeof option.value === 'number';
                      return option.value === (isNumber ? Number(selected) : selected);
                    });
                    return (
                      <Typography noWrap>
                        <>{selectedOption?.label || selected}</>
                      </Typography>
                    );
                  }

                  const selectedLabels = selected
                    .map((value) => options.find((option) => option.value === value)?.label)
                    .filter(Boolean)
                    .join(', ');

                  return <Typography noWrap>{selectedLabels}</Typography>;
                }}
                value={selectProps.multiple ? (Array.isArray(field.value) ? field.value : []) : field.value}
              >
                {handleAddNewOption && (
                  <MenuItem
                    value=''
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddNewOption();
                    }}
                  >
                    <Box
                      color='secondary'
                      display='flex'
                      justifyContent='center'
                      alignItems='center'
                      width='100%'
                      border='1px solid #F3F3F3'
                    >
                      <BiPlus />
                      <Typography>Add new option</Typography>
                    </Box>
                  </MenuItem>
                )}
                {options.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled || false}
                    selected={option.selected || false}
                  >
                    <Box display='flex' justifyContent='space-between' alignItems='center' width='100%'>
                      <Typography>{option.label}</Typography>
                      {option.audioUrl && (
                        <>
                          {playingAudio === option.value ? (
                            <MdStopCircle
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePlayAudio(option.audioUrl, option.value);
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                          ) : (
                            <FaRegCirclePlay
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePlayAudio(option.audioUrl, option.value);
                              }}
                              style={{ cursor: 'pointer' }}
                            />
                          )}
                          <audio ref={(el) => (audioRefs.current[option.value] = el)} src={option.audioUrl} />
                        </>
                      )}
                    </Box>
                  </MenuItem>
                ))}
                {isLoading && (
                  <MenuItem disabled>
                    <Box display='flex' justifyContent='center' alignItems='center' width='100%'>
                      <Typography>Loading...</Typography>
                    </Box>
                  </MenuItem>
                )}
              </SelectComponent>
              <FormHelperText sx={{ color: theme.palette.error.main }}>{error ? error.message : null}</FormHelperText>
            </>
          );
        }}
      />
    </FormControl>
  );
}
